import {
  titleUI,
  textUI,
  addressUI,
  numberUI,
  currentOrPastDateUI,
  currentOrPastDateSchema,
  addressSchema,
  numberSchema,
  textSchema,
} from 'platform/forms-system/src/js/web-component-patterns';

import { EmploymentHistoryView } from '../components/viewElements';
import SafeArrayField from '../components/SafeArrayField';
import { wrapDateUiWithDl } from '../helpers/reviewHelpers';
import { HideDefaultDateHint } from '../helpers/dateHint';
import { veteranFields } from '../definitions/constants';
import { validateDateRange } from '../helpers/dateRangeCustom';

const DEFAULT_DL_TITLES = {
  city: 'City',
  state: 'State',
};

const shouldUseEmploymentDateValidation = formData =>
  !!formData?.form218940EmployerDateValidation;

const addUseDlWrap = (field, key) => {
  if (!field) {
    return field;
  }

  const updatedField = {
    ...field,
    'ui:options': {
      ...field['ui:options'],
      useDlWrap: true,
    },
  };

  if (key && DEFAULT_DL_TITLES[key] && !updatedField['ui:title']) {
    updatedField['ui:title'] = DEFAULT_DL_TITLES[key];
  }

  return updatedField;
};

const addDateRangeError = ({
  errors,
  fromDate,
  toDate,
  formData,
  errorMessage,
}) => {
  if (!errors || !fromDate || !toDate) {
    return;
  }

  validateDateRange(
    { to: errors },
    { from: fromDate, to: toDate },
    formData,
    undefined,
    {
      pattern: errorMessage,
    },
    true,
  );
};

const validateEmploymentHistoryDates = (
  errors,
  employersHistory = [],
  formData = {},
) => {
  if (
    !shouldUseEmploymentDateValidation(formData) ||
    !Array.isArray(employersHistory)
  ) {
    return;
  }

  const dateOfBirth =
    formData?.[veteranFields.parentObject]?.[veteranFields.dateOfBirth];

  employersHistory.forEach((employer, index) => {
    const itemErrors = errors?.[index];

    if (!employer || !itemErrors) {
      return;
    }

    const { startDate, endDate } = employer;

    addDateRangeError({
      errors: itemErrors.startDate,
      fromDate: dateOfBirth,
      toDate: startDate,
      formData,
      errorMessage:
        "Employment start date can't be before the Veteran's date of birth",
    });

    addDateRangeError({
      errors: itemErrors.endDate,
      fromDate: dateOfBirth,
      toDate: endDate,
      formData,
      errorMessage:
        "Employment end date can't be before the Veteran's date of birth",
    });

    addDateRangeError({
      errors: itemErrors.endDate,
      fromDate: startDate,
      toDate: endDate,
      formData,
      errorMessage:
        'Employment end date must be on or after the employment start date',
    });
  });
};

const addressWithDlWrap = uiSchema =>
  Object.keys(uiSchema).reduce((acc, key) => {
    const value = uiSchema[key];
    if (key.startsWith('ui:') || key.startsWith('view:')) {
      acc[key] = value;
      return acc;
    }

    acc[key] = addUseDlWrap(value, key);
    return acc;
  }, {});

/** @type {PageSchema} */
export default {
  uiSchema: {
    ...titleUI(
      'Your employment history',
      `List all employment and self-employment from the past 5 years. If you haven’t worked recently, start with your most recent job and provide 5 years of employment history from that date. Include all military duty, including inactive duty for training.`,
    ),
    employersHistory: {
      'ui:field': SafeArrayField,
      'ui:validations': [validateEmploymentHistoryDates],
      'ui:options': {
        itemName: 'employer',
        viewField: EmploymentHistoryView,
        customTitle: 'Your employers',
        useDlWrap: true,
        keepInPageOnReview: true,

        doNotScroll: true,
        confirmRemove: true,
        confirmRemoveDescription:
          'Are you sure you want to remove this employer?',
        itemAriaLabel: item => `employer: ${item?.employerName || 'Employer'}`,
      },
      items: {
        'ui:options': {
          classNames: 'vads-u-margin-left--1p5',
        },
        employerName: textUI({
          title: "Employer's name",
          useDlWrap: true,

          errorMessages: {
            required:
              'Enter the employer name, for example, Local Grocery Store',
            pattern:
              'Enter the employer name, for example, Local Grocery Store',
          },
        }),

        employerAddress: addressWithDlWrap(
          addressUI({
            title: 'Employer address',
            omit: ['street2', 'street3', 'isMilitary'],
          }),
        ),
        typeOfWork: textUI({
          title: 'Type of work',
          useDlWrap: true,
          hint: 'Describe the kind of work you did. For example: cashier at a grocery store, part-time landscaper, or office assistant.',

          maxlength: 62,
          charcount: true,

          errorMessages: {
            required:
              'Enter the kind of work you did or are doing for this employer',
            pattern:
              'Enter the kind of work you did or are doing for this employer',
          },
        }),
        hoursPerWeek: numberUI({
          title: 'Hours worked per week',
          useDlWrap: true,
          max: 168,
          hint: 'Enter the number of hours you worked each week (maximum 168 hours)',
          errorMessages: {
            max: 'Hours worked per week cannot exceed 168 hours',

            required:
              'Enter the number of hours you worked or work per week for this employer',
          },
        }),
        startDate: {
          ...wrapDateUiWithDl(
            currentOrPastDateUI({
              title: 'Start date',
              hint: 'For example: January 19 2022',
            }),
          ),
          'ui:description': HideDefaultDateHint,
        },
        endDate: {
          ...wrapDateUiWithDl(
            currentOrPastDateUI({
              title: 'End date (if applicable)',
              hint: 'For example: January 19 2022',
            }),
          ),
          'ui:description': HideDefaultDateHint,
        },
        timeLost: numberUI({
          title: 'Time lost from illness',
          useDlWrap: true,
          hint: 'Enter the total number of workdays you missed because of illness. Use whole numbers only (for example, 100).',
          errorMessages: {
            required:
              'Enter the total number of days lost because of illness at this employer. Enter whole numbers only.',
            pattern:
              'Enter the total number of days lost because of illness at this employer. Enter whole numbers only.',
          },
        }),
        earnings: numberUI({
          title: 'Highest gross earnings per month',
          hint: 'Numeric characters only',
          useDlWrap: true,
          errorMessages: {
            required:
              'Enter the most you earned per month at this employer. Enter whole numbers only.',
            pattern:
              'Enter the most you earned per month at this employer. Enter whole numbers only.',
          },
        }),
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      employersHistory: {
        type: 'array',
        minItems: 1,
        maxItems: 4,
        items: {
          type: 'object',
          properties: {
            employerName: textSchema,

            employerAddress: addressSchema({
              omit: ['street2', 'street3', 'isMilitary'],
            }),
            typeOfWork: textSchema,
            hoursPerWeek: numberSchema,
            startDate: currentOrPastDateSchema,
            endDate: currentOrPastDateSchema,
            timeLost: numberSchema,
            earnings: numberSchema,
          },
          required: [
            'employerName',
            'employerAddress',
            'typeOfWork',
            'hoursPerWeek',
            'startDate',
            'timeLost',
            'earnings',
          ],
        },
      },
    },
  },
};
